import { useTranslation } from "react-i18next";
import { colors } from "../../../data/colors";
import { stdObject } from "../../../data/types/stdObject";
import { toPrice } from "../../../utils/toPrice";
import convertDate from "../../../utils/convertDate";
import { convertNumberDecimal } from "../../../utils/convertNumberDecimal";
import BasicButton from "../../../components/BasicButton";
import { useMainContext } from "../../../context/MainContext";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import PasswordIcon from "@mui/icons-material/Password";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  alignList,
  langsList,
  searchMode,
  statusTitles,
} from "../../../data/main";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SquareIcon from "@mui/icons-material/Square";
import { userTypes } from "../../../data/types/userTypes";
import { arrayToObject } from "../../../utils/arrayToObject";

export default function StdTable({
  type,
  align = "left",
  style = {},
  mainHeader = "",
  headerColor = "",
  header = [],
  data = [],
  totals = null,
  className = "",
  onDoubleClick = () => {},
}: {
  type: string;
  align?: any;
  style: stdObject;
  mainHeader?: string;
  headerColor?: string;
  header: Array<stdObject>;
  data: Array<stdObject>;
  totals?: stdObject | null;
  className?: string;
  onDoubleClick?: (v: stdObject, index: number) => void;
}) {
  const { t } = useTranslation();

  const user: userTypes = useSelector((state: RootState) => state.data.user);
  const usersList: stdObject = useSelector(
    (state: RootState) => state.data.usersList,
  );
  const rolsList: Array<stdObject> = useSelector(
    (state: RootState) => state.data.rol,
  );
  const sucsList: Array<stdObject> = useSelector(
    (state: RootState) => state.data.suc,
  );
  const bankAccountsList: Array<stdObject> = useSelector(
    (state: RootState) => state.data.bankAccounts,
  );
  const searchList: any = useSelector((state: RootState) => state.data.search);
  const statusDict: stdObject = arrayToObject({
    key: "CODPAG",
    erase: ["CODPAG"],
    data: searchList.docStatus,
  });
  const { setMsg, setWindowValues } = useMainContext();

  const writeTotal = (data: any) => {
    if (typeof data === "object" && "type" in data) {
      const filteredData = data.values.filter((v: stdObject) => v.ACTIVE);
      return (
        <BasicButton
          onClick={() => {
            setMsg("approveCommModal");
            setWindowValues({
              data: {
                id: data.values[0].AGECOY,
                name: data.values[0].ANOCOY,
                serie: user.CODSUC,
                codes: filteredData.map((e: stdObject) => [
                  e.DOCCOY,
                  e.SERCOY,
                  e.CODCOY,
                  e.LINCOY,
                  e.AGECOY,
                  e.GRUCOY,
                ]),
                total: filteredData.reduce(
                  (acc: number, curr: stdObject) => (acc += curr.COMMTOTCOY),
                  0,
                ),
              },
            });
          }}
          title={data.label}
          style={data.style}
        />
      );
    } else if (typeof data === "string" || typeof data === "number") {
      return toPrice(data);
    }
    return data;
  };

  const writeByType = (index: number, v: stdObject, e: stdObject) => {
    switch (e.type) {
      case "checkbox":
        return v[e.code] ? (
          <CheckBoxIcon
            style={{ color: colors.green }}
            onClick={() => e.onClick(v)}
          />
        ) : (
          <CheckBoxOutlineBlankIcon
            style={{ color: colors.grey }}
            onClick={() => e.onClick(v)}
          />
        );
      case "lang":
        const langCode = langsList.find(
          (o: stdObject) => o.CODIDI === v[e.code],
        )?.NOMIDI;
        return langCode ? t(`p.${langCode}`) : "";
      case "color":
        return <SquareIcon style={{ color: v[e.code] }} />;
      case "table":
        return (
          <table>
            <tbody>
              {v[e.code].map((o: any[], index: number) => (
                <tr key={index}>
                  {o.map((p: any, idx: number) => (
                    <td key={idx}>{p}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "icon":
        return <e.Icon onClick={v[e.code].onClick} style={v[e.code].style} />;
      case "reset":
        return v.BLOUSU && Number(v.CODUSU) !== user.CODUSU ? (
          ""
        ) : (
          <PasswordIcon
            style={{ color: colors.red }}
            onClick={() => e.reset(index, v)}
          />
        );
      case "delete":
        return (
          <DeleteForeverIcon
            style={{ color: colors.red }}
            onClick={() => e.delete(index, v)}
          />
        );
      case "rol":
        const rolCode = rolsList.find(
          (o: stdObject) => o.CODROL === v[e.code],
        )?.NOMROL;
        return rolCode ?? "";
      case "users":
        return usersList[v[e.code]]?.NOMUSU ?? "";
      case "countries":
        return v[e.code] === "" ? t("p.unknown") : t(`country.${v[e.code]}`);
      case "devices":
        return v[e.code] === "unknown" ? t("p.unknown") : v[e.code];
      case "bankAccounts":
        if (v[e.code] === 0) {
          return t("p.pettyCash");
        }
        const accountCode = bankAccountsList.find(
          (o: stdObject) => o.CODCUE === v[e.code],
        )?.NOMCUE;
        return accountCode ?? "";
      case "branches":
        const branchCode = sucsList.find(
          (o: stdObject) => o.CODSUC === v[e.code],
        )?.NOMSUC;
        return branchCode ?? "";
      case "branches2":
        const branchCode2 = sucsList.find(
          (o: stdObject) => o.CODSUC === v[e.code],
        )?.NOMSUC;
        return branchCode2 ?? t("p.all");
      case "recordName":
        return Array.isArray(v[e.code]) ? (
          <table align="center">
            {v[e.code].map((m: string[], index: number) => (
              <tr key={index}>
                {m.map((b: string, idx: number) => (
                  <td key={idx} align={idx === 0 ? "left" : "right"}>
                    {b}
                  </td>
                ))}
              </tr>
            ))}
          </table>
        ) : (
          v[e.code]
        );
      case "button":
        return (
          <BasicButton
            onClick={v[e.code].onClick}
            title={v[e.code].label}
            style={v[e.code].style}
          />
        );
      case "commission":
        return convertNumberDecimal(v[e.code]);
      case "percentage":
        return v[e.code] + "%";
      case "price":
        return v[e.code] !== 0 ? toPrice(v[e.code]) : "-";
      case "date":
        return convertDate({ info: v[e.code], t });
      case "issuesType":
        return t(
          statusTitles.find((o: stdObject) => o.code === v[e.code])?.label ??
            "p.title",
        );
      case "mode":
        return t(searchMode[v[e.code]]);
      case "align":
        return t(
          alignList.find((o: stdObject) => o.ALICAB === Number(v[e.code]))
            ?.ALINOM ?? v[e.code],
        );
      case "statusType":
        return v[e.code] === -1 ? "" : statusDict[v[e.code]]?.NOMPAG;
      default:
        return v[e.code];
    }
  };

  return (
    <table cellSpacing={0} cellPadding={5} align={align} style={style}>
      <thead>
        {mainHeader !== "" && (
          <tr className="header">
            <th
              className="noSelect"
              colSpan={header.length}
              style={{ backgroundColor: colors[type], color: headerColor }}
            >
              {mainHeader}
            </th>
          </tr>
        )}
        {header.length > 0 && (
          <tr className="header">
            {header.map((e: stdObject, index: number) => (
              <th
                key={index}
                className="noSelect"
                style={{ backgroundColor: colors[type], ...e.style }}
              >
                {t(e.label)}
              </th>
            ))}
          </tr>
        )}
      </thead>
      <tbody>
        {data.map((v: stdObject, index: number) => (
          <tr
            className={className}
            key={index}
            style={{
              backgroundColor:
                v.BGCOLOR ?? (index % 2 ? colors.white : colors.lightGrey),
              ...(v.style ?? {}),
            }}
            onDoubleClick={() => onDoubleClick(v, index)}
          >
            {header.map((e: stdObject, idx: number) => (
              <td
                key={idx}
                style={{
                  textAlign: e.align,
                  borderRight: e.lineClass ? `solid 1px ${colors.black}` : "",
                  ...(e.cellStyle ?? {}),
                }}
              >
                {writeByType(index, v, e)}
              </td>
            ))}
          </tr>
        ))}

        {totals && (
          <tr>
            {header.map((e: stdObject, index: number) => (
              <td
                key={index}
                className="noSelect"
                style={{
                  backgroundColor: colors.grey,
                  textAlign: e.align,
                  borderRight: e.lineClass ? `solid 1px ${colors.black}` : "",
                }}
              >
                {e.code in totals ? writeTotal(totals[e.code]) : ""}
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
}
